import React, { useState } from 'react';
import useRequest from '@hooks/useRequest';
import { UploadedDrawingFileType } from '@modules/request';
import ConnectDriveDialogPresenter from './ConnectDriveDialogPresenter';
import ConnectDriveDropzone from './ConnectDriveDropzone';
import ConnectDriveList from './ConnectDriveList';
import ConnectDriveUploadButton from './ConnectDriveUploadButton';
import usePathId from './usePathId';
import useSelectFile from './useSelectFile';
import { ConnectDriveDialogProps } from './types';
import {
  CapaConnectFolder,
  CapaConnectMyFile,
  useFetchConnectMyDriveQuery,
  useSearchFileQuery
} from '@generated/graphql';
import useKeyword from './useKeyword';
import ConnectDriveSearchBar from './ConnectDriveSearchBar';

const ConnectDriveDialogContainer = ({
  open,
  matches,
  serviceType,
  onClose
}: Pick<
  ConnectDriveDialogProps,
  'open' | 'onClose' | 'serviceType' | 'matches'
>) => {
  const {
    requestState: { uploadedDrawingFiles },
    onUpdateRfqInfo
  } = useRequest();
  const [paths, setPaths] = useState<CapaConnectFolder[]>([]);
  const [selectedFile, onSelect] = useSelectFile();
  const [pathId, setPathId] = usePathId();
  const [keyword, setKeyword] = useKeyword();

  const isSearch = keyword ? true : false;

  const { data, loading, refetch } = useFetchConnectMyDriveQuery({
    variables: {
      input: {
        isLatest: false,
        pathId: pathId
      }
    },
    onCompleted: ({ fetchConnectMyDrive }) => {
      if (fetchConnectMyDrive.data?.path) {
        setPaths(fetchConnectMyDrive.data?.path);
      }
    },
    skip: isSearch
  });

  const { data: searchedData } = useSearchFileQuery({
    variables: {
      input: {
        pathId: pathId,
        keyword
      }
    },
    skip: !isSearch
  });

  const onChangePath = (pathId: string) => setPathId(pathId);

  const onRefetchMyDrive = async () => {
    await refetch();
  };

  const onSubmit = () => {
    if (selectedFile.length === 0) return;

    const convertFile: UploadedDrawingFileType[] = selectedFile.map((file) => ({
      fileId: file.fileId ?? '',
      fileName: file.fileName,
      isValid: false
    }));

    onUpdateRfqInfo({
      uploadedDrawingFiles: [...uploadedDrawingFiles, ...convertFile]
    });

    return onClose();
  };

  const files: Omit<CapaConnectMyFile, 'fileExtension'>[] = keyword
    ? searchedData?.searchFile.data?.files ?? []
    : data?.fetchConnectMyDrive.data?.files ?? [];

  const isEmpty =
    !loading && !isSearch && files.length === 0 && paths.length === 1;

  return (
    <ConnectDriveDialogPresenter
      matches={matches}
      open={open}
      files={files}
      onSubmit={onSubmit}
      onClose={onClose}
      submitDisabled={!selectedFile.length}
    >
      {isEmpty && <ConnectDriveDropzone onRefetchMyDrive={onRefetchMyDrive} />}

      {!isEmpty && (
        <>
          <ConnectDriveSearchBar keyword={keyword} onChange={setKeyword} />

          <ConnectDriveUploadButton
            matches={matches}
            currentPath={pathId}
            onRefetchMyDrive={onRefetchMyDrive}
          />
          <ConnectDriveList
            matches={matches}
            files={files ?? []}
            folders={paths ?? []}
            selectedFile={selectedFile}
            serviceType={serviceType}
            onSelect={onSelect}
            onChangePath={onChangePath}
          />
        </>
      )}
    </ConnectDriveDialogPresenter>
  );
};

export default ConnectDriveDialogContainer;
